import { io, Socket } from 'socket.io-client';
import type { UserStatus } from '@/types';
import type { Call } from '@/api/calls';

export interface ClientToServerEvents {
  'channel:join': (data: { channelId: string }) => void;
  'channel:leave': (data: { channelId: string }) => void;
  // Phase 7 — clientMessageId это идемпотентный ключ от клиента (UUIDv4).
  // Если задан, повторная отправка не создаёт дубликат.
  'message:send': (data: { channelId: string; content: string; type?: string; replyToId?: string; clientMessageId?: string }) => void;
  'dm:send': (data: { recipientId: string; content: string; clientMessageId?: string; replyToId?: string }) => void;
  'typing:start': (data: { channelId: string }) => void;
  'typing:stop': (data: { channelId: string }) => void;
  'dm:typing:start': (data: { recipientId: string }) => void;
  'dm:typing:stop': (data: { recipientId: string }) => void;
  'message:read': (data: { messageId: string }) => void;
  'status:set': (data: { status: UserStatus }) => void;

  // ── Calls ────────────────────────────────────────────────────────────────
  'call:start': (data: { calleeId: string; mode: 'VOICE' | 'VIDEO' }) => void;
  'call:accept': (data: { callId: string }) => void;
  'call:reject': (data: { callId: string }) => void;
  'call:cancel': (data: { callId: string }) => void;
  'call:hangup': (data: { callId: string; reason?: string }) => void;
  'call:offer': (data: { callId: string; sdp: string }) => void;
  'call:answer': (data: { callId: string; sdp: string }) => void;
  'call:ice-candidate': (data: { callId: string; candidate: unknown }) => void;
}

export interface ServerToClientEvents {
  'message:new': (message: unknown) => void;
  'message:edited': (message: unknown) => void;
  'message:deleted': (data: { id: string; channelId: string }) => void;
  'dm:new': (message: unknown) => void;
  // Phase 16 — DM delete / clear events.
  'dm:deleted': (data: { dmId: string; peerId: string; localOnly?: boolean }) => void;
  'dm:cleared': (data: { peerId: string; scope: 'me' | 'all' | 'peer-all' }) => void;
  'member:joined': (data: { channelId: string; member: unknown }) => void;
  'member:left': (data: { channelId: string; userId: string }) => void;
  'member:kicked': (data: { channelId: string; userId: string; reason?: string }) => void;
  'member:banned': (data: { channelId: string; userId: string; reason?: string }) => void;
  'typing:update': (data: { channelId: string; users: string[] }) => void;
  'dm:typing': (data: { fromUserId: string; isTyping: boolean }) => void;
  'user:status': (data: { userId: string; status: UserStatus }) => void;
  'notification:new': (notification: unknown) => void;

  // ── Calls (server → client) ──────────────────────────────────────────────
  'call:incoming': (data: { call: Call }) => void;
  'call:state': (data: { call: Call }) => void;
  'call:offer': (data: { callId: string; fromUserId: string; sdp: string }) => void;
  'call:answer': (data: { callId: string; fromUserId: string; sdp: string }) => void;
  'call:ice-candidate': (data: { callId: string; fromUserId: string; candidate: unknown }) => void;
  'call:error': (data: { callId?: string; code: string; message: string }) => void;

  'error': (data: { message: string; code: string }) => void;
}

export type AppSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

let socket: AppSocket | null = null;

export function getSocket(): AppSocket {
  if (!socket) {
    // Derive socket.io path from Vite's base URL
    const base = import.meta.env.BASE_URL ?? '/';
    const socketPath = base.replace(/\/$/, '') + '/socket.io';
    socket = io(import.meta.env.VITE_WS_URL ?? '', {
      path: socketPath,
      autoConnect: false,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });
  }
  return socket;
}

export function connectSocket(token: string): AppSocket {
  const s = getSocket();
  s.auth = { token };
  if (!s.connected) s.connect();
  return s;
}

export function disconnectSocket(): void {
  socket?.disconnect();
  socket = null;
}
